import { useEffect } from 'react'
import type { FieldValues } from 'react-hook-form'
import { useWatch, type Path, type UseFormReturn } from 'react-hook-form'
import type z from 'zod'

/**
 * Hook to watch for form value updates.
 * This should be used instead of the form.watch function from react-hook-form's useForm.
 * This is required for React Compiler, since some functions of react-hook-form
 * does not yet support it properly.
 */
export function useFormWatch<
  TFormValues extends FieldValues,
  TKey extends Path<TFormValues>,
>(form: UseFormReturn<TFormValues>, name: TKey) {
  return useWatch({ control: form.control, name })
}

export function useFormQueryField<
  TFormValues extends FieldValues,
  TKey extends Path<TFormValues>,
>(form: UseFormReturn<TFormValues>, name: TKey) {
  const watched = useFormWatch(form, name)

  useQueryFieldValue(name, watched)
}

export function useQueryFieldValue(
  name: string,
  value: string | number | boolean | string[] | null | undefined
) {
  useEffect(() => {
    if (typeof value === 'string' && !value) return
    if (Array.isArray(value) && value.length === 0) return
    if (value === null || value === undefined) return

    const query = new URLSearchParams(window.location.search)

    const valueString = Array.isArray(value)
      ? value.map(v => String(v)).join(',')
      : String(value)

    query.set(name, valueString)
    const newUrl = `${window.location.pathname}?${query.toString()}`
    window.history.replaceState(null, '', newUrl)
  }, [name, value])
}

export function useParseQueryState<TSchema extends z.ZodObject>(
  schema: TSchema,
  ...keys: (keyof z.output<TSchema>)[]
): Partial<z.output<TSchema>> {
  const query = new URLSearchParams(window.location.search)
  const obj = schema.partial().safeParse(Object.fromEntries(query.entries()))
  if (obj.success) {
    if (keys.length === 0) return obj.data as Partial<z.output<TSchema>>
    return keys.reduce(
      (acc, key) => {
        if (key in obj.data) acc[key] = obj.data[key as any]
        return acc
      },
      {} as Partial<z.output<TSchema>>
    )
  }
  return {}
}
